import type {
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  ViewChild,
  inject,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { PipesModule } from '@freelancer/pipes';
import { toNumber } from '@freelancer/utils';
import type { Subscription } from 'rxjs';
import { fromEvent } from 'rxjs';
import { IconColor, IconModule, IconSize } from '../icon';
import { SliderLabelPosition } from './slider.types';

@Component({
  standalone: true,
  imports: [IconModule, PipesModule],
  selector: 'fl-slider',
  template: `
    <div class="SliderContainer">
      <div
        class="SliderRailBackground"
        #railBackground
        (click)="railClicked($event)"
        (touchend)="railClicked($event)"
      ></div>
      <div
        class="SliderRailHighlight"
        #railHighlight
        (click)="railClicked($event)"
        (touchend)="railClicked($event)"
      ></div>

      @if (showMinHandle) {
        <div
          class="SliderHandle"
          #minHandle
          role="slider"
          tabindex="0"
          [attr.title]="minAriaLabel"
          [attr.aria-label]="minAriaLabel"
          [attr.aria-valuemin]="minValue"
          [attr.aria-valuemax]="maxValue"
          [attr.aria-valuenow]="minControl.value"
          (keydown)="minHandleKey($event)"
          (mousedown)="minHandleClicked($event)"
          (touchstart)="minHandleTouched($event)"
        >
          <fl-icon
            [name]="'ui-code'"
            [color]="IconColor.LIGHT"
            [size]="IconSize.SMALL"
            [useIconFont]="true"
            [fillIconFont]="true"
          ></fl-icon>
        </div>
      }
      @if (showMaxHandle) {
        <div
          class="SliderHandle"
          #maxHandle
          role="slider"
          tabindex="0"
          [attr.title]="maxAriaLabel"
          [attr.aria-label]="maxAriaLabel"
          [attr.aria-valuemin]="minValue"
          [attr.aria-valuemax]="maxValue"
          [attr.aria-valuenow]="maxControl.value"
          (keydown)="maxHandleKey($event)"
          (mousedown)="maxHandleClicked($event)"
          (touchstart)="maxHandleTouched($event)"
        >
          <fl-icon
            [name]="'ui-code'"
            [color]="IconColor.LIGHT"
            [size]="IconSize.SMALL"
            [useIconFont]="true"
            [fillIconFont]="true"
          ></fl-icon>
        </div>
      }
    </div>
    @if (labelPosition !== SliderLabelPosition.NONE) {
      <div
        class="SliderLabel"
        [attr.data-label-position]="labelPosition"
      >
        @if (currencyCode) {
          @if (labelPosition === SliderLabelPosition.CENTER) {
            <p>
              {{
                {
                  minimum: minControl.value,
                  maximum: maxControl.value,
                } | flCurrency: currencyCode
              }}
            </p>
          }
          @if (labelPosition === SliderLabelPosition.SPACE_BETWEEN) {
            <p>
              {{ minControl.value | flCurrency: currencyCode }}
            </p>
            <p>
              {{ maxControl.value | flCurrency: currencyCode }}
            </p>
          }
        } @else {
          @if (showMinHandle) {
            <p>
              {{ minControl.value }}
            </p>
          }
          @if (
            showMinHandle &&
            showMaxHandle &&
            labelPosition === SliderLabelPosition.CENTER
          ) {
            <p class="SliderLabel-divider">
              <!--
              FIXME: Remove template expression when @angular-eslint/template/i18n
              can safely ignore these characters. See upstream bug in
              https://github.com/angular-eslint/angular-eslint/issues/298
              -->
              {{ '-' }}
            </p>
          }
          @if (showMaxHandle) {
            <p>
              {{ maxControl.value }}
            </p>
          }
        }
      </div>
    }
  `,
  styleUrls: ['./slider.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SliderComponent implements OnInit, OnDestroy, OnChanges {
  private changeDetectorRef = inject(ChangeDetectorRef);

  IconColor = IconColor;
  IconSize = IconSize;
  SliderLabelPosition = SliderLabelPosition;

  showMinHandle = true;
  showMaxHandle = true;
  minHandleActive = false;
  maxHandleActive = false;
  barLeft = 0;
  barRight = 0;
  activeListener: any;

  @Input() minValue: number;
  @Input() maxValue: number;
  @Input() minControl: FormControl<number>;
  @Input() maxControl: FormControl<number>;
  @Input() currencyCode?: string;
  @Input() units = '';
  @Input() labelPosition = SliderLabelPosition.CENTER;

  @Input() minAriaLabel = 'Minimum value';
  @Input() maxAriaLabel = 'Maximum value';

  @ViewChild('railHighlight', { read: ElementRef, static: true })
  railHighlight: ElementRef<HTMLDivElement>;
  @ViewChild('railBackground', { read: ElementRef, static: true })
  railBackground: ElementRef<HTMLDivElement>;

  @ViewChild('minHandle', { read: ElementRef })
  minHandleEl?: ElementRef<HTMLDivElement>;
  @ViewChild('maxHandle', { read: ElementRef })
  maxHandleEl?: ElementRef<HTMLDivElement>;

  private minControlSubscription?: Subscription;
  private maxControlSubscription?: Subscription;
  private mouseMoveEventSubscription?: Subscription;
  private touchMoveEventSubscription?: Subscription;
  private selectStartSubscription?: Subscription;

  ngOnInit(): void {
    if (!this.minControl) {
      this.minControl = new FormControl(this.minValue, {
        nonNullable: true,
      });
      this.showMinHandle = false;
    }
    if (!this.maxControl) {
      this.maxControl = new FormControl(this.maxValue, {
        nonNullable: true,
      });
      this.showMaxHandle = false;
    }

    if (this.minControl && this.minControl.value === null) {
      this.minControl.setValue(this.minValue);
    }
    if (this.maxControl && this.maxControl.value === null) {
      this.maxControl.setValue(this.maxValue);
    }
    setTimeout(() => this.updateView(), 0);

    this.minControlSubscription = this.minControl.valueChanges.subscribe(() =>
      this.updateView(),
    );
    this.maxControlSubscription = this.maxControl.valueChanges.subscribe(() =>
      this.updateView(),
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Manually set the value of `minControl` if it is not bound to an input
    if (!this.showMinHandle && changes.minValue && this.minControl) {
      this.minControl.setValue(changes.minValue.currentValue);
    }

    // Manually set the value of `maxControl` if it is not bound to an input
    if (!this.showMaxHandle && changes.maxValue && this.maxControl) {
      this.maxControl.setValue(changes.maxValue.currentValue);
    }
  }

  ngOnDestroy(): void {
    this.minControlSubscription?.unsubscribe();
    this.maxControlSubscription?.unsubscribe();
    this.mouseMoveEventSubscription?.unsubscribe();
    this.touchMoveEventSubscription?.unsubscribe();
    this.selectStartSubscription?.unsubscribe();
  }

  @HostListener('document:mouseup', ['$event'])
  onMouseLeave(event: MouseEvent): void {
    this.minHandleActive = false;
    this.maxHandleActive = false;
    this.mouseMoveEventSubscription?.unsubscribe();
    this.selectStartSubscription?.unsubscribe();
  }

  /*
   * `any` is needed to prevent `ReferenceError: TouchEvent is not defined.`
   *  in desktop Firefox
   */
  @HostListener('touchend', ['$event'])
  ontouchend(event: TouchEvent | any): void {
    if (event instanceof TouchEvent) {
      this.minHandleActive = false;
      this.maxHandleActive = false;
      this.touchMoveEventSubscription?.unsubscribe();

      // Prevents mouse events from being dispatched
      if (event.cancelable) {
        event.preventDefault();
      }
    }
  }

  minHandleClicked(e: MouseEvent): void {
    if (this.minControl) {
      this.handleClicked(e, this.minControl);
    }
  }

  maxHandleClicked(e: MouseEvent): void {
    if (this.maxControl) {
      this.handleClicked(e, this.maxControl);
    }
  }

  minHandleTouched(event: TouchEvent): void {
    if (this.minControl) {
      this.handleTouched(event, this.minControl);
    }
  }

  maxHandleTouched(event: TouchEvent): void {
    if (this.maxControl) {
      this.handleTouched(event, this.maxControl);
    }
  }

  minHandleKey(event: KeyboardEvent): void {
    if (this.minControl) {
      this.handleKeyEvent(event, this.minControl);
    }
  }

  maxHandleKey(event: KeyboardEvent): void {
    if (this.maxControl) {
      this.handleKeyEvent(event, this.maxControl);
    }
  }

  railClicked(e: MouseEvent | TouchEvent): void {
    this.setUpBarLimits();
    let control: FormControl<number> | undefined;
    if (this.showMinHandle && !this.showMaxHandle) {
      control = this.minControl;
    } else if (!this.showMinHandle && this.showMaxHandle) {
      control = this.maxControl;
    }
    if (control) {
      control.setValue(this.getValue(e, control));
    }
  }

  setUpBarLimits(): void {
    const rect = this.railBackground.nativeElement.getBoundingClientRect();
    this.barLeft = rect.left;
    this.barRight = rect.right;
  }

  handleClicked(e: MouseEvent, control: FormControl<number>): void {
    this.setUpBarLimits();
    this.minHandleActive = true;
    this.selectStartSubscription = fromEvent(document, 'selectstart').subscribe(
      disableSelect,
    );
    this.activeListener = this.onMouseMove(control);
    this.mouseMoveEventSubscription = fromEvent(
      document,
      'mousemove',
    ).subscribe(this.activeListener);
  }

  handleTouched(e: TouchEvent, control: FormControl<number>): void {
    this.setUpBarLimits();
    this.minHandleActive = true;

    this.activeListener = this.onTouchMove(control);
    this.touchMoveEventSubscription = fromEvent(
      document,
      'touchmove',
    ).subscribe(this.activeListener);

    // Prevents window from scrolling
    e.preventDefault();
  }

  handleKeyEvent(e: KeyboardEvent, control: FormControl<number>): void {
    this.setUpBarLimits();
    const minBound = this.getMinBound(control);
    const maxBound = this.getMaxBound(control);

    const k = e.key?.toLowerCase();
    // needs casting because inferred type is just string
    switch (k) {
      case 'arrowdown':
      case 'arrowleft':
      case 'down': // IE name
      case 'left':
        control.setValue(Math.max(toNumber(control.value) - 1, minBound));
        // prevent defaults like scrolling on up/down
        e.preventDefault();
        break;
      case 'arrowup':
      case 'arrowright':
      case 'up':
      case 'right':
        control.setValue(Math.min(toNumber(control.value) + 1, maxBound));
        e.preventDefault();
        break;
      case 'pagedown':
        control.setValue(Math.max(toNumber(control.value) - 10, minBound));
        e.preventDefault();
        break;
      case 'pageup':
        control.setValue(Math.max(toNumber(control.value) + 10, minBound));
        e.preventDefault();
        break;
      default:
    }
  }

  getValue(
    event: MouseEvent | TouchEvent,
    control: FormControl<number>,
  ): number {
    let xCoord;

    if (event instanceof MouseEvent) {
      xCoord = event.x;
    } else if (event instanceof TouchEvent) {
      const touchPoint = event.changedTouches.item(0);

      if (touchPoint) {
        xCoord = touchPoint.clientX;
      }
    }

    if (!xCoord) {
      return NaN;
    }

    let progress = (xCoord - this.barLeft) / (this.barRight - this.barLeft);
    progress = Math.min(Math.max(progress, 0), 1);
    const value = this.minValue + (this.maxValue - this.minValue) * progress;
    const minBound = this.getMinBound(control);
    const maxBound = this.getMaxBound(control);

    return Math.min(Math.max(Math.round(value), minBound), maxBound);
  }

  /** Returns the maximum value for a control */
  getMaxBound(control: FormControl<number>): number {
    return this.maxControl && this.maxControl !== control
      ? // dual slider and this is the min control: value is the max control value
        this.maxControl.value
      : // otherwise, value is the global max
        this.maxValue;
  }

  /** Returns the minimum value for a control */
  getMinBound(control: FormControl<number>): number {
    return this.minControl && this.minControl !== control
      ? // dual slider and this is the max control: value is the min control value
        this.minControl.value
      : // otherwise, value is the global min
        this.minValue;
  }

  onMouseMove(control: FormControl<number>) {
    return (e: MouseEvent) => {
      control.setValue(this.getValue(e, control));
      control.markAsDirty();
    };
  }

  onTouchMove(control: FormControl<number>) {
    return (event: TouchEvent) => {
      control.setValue(this.getValue(event, control));
      control.markAsDirty();
    };
  }

  updateView(): void {
    let leftPos = 0;
    let rightPos = 100;
    const range = this.maxValue - this.minValue;
    if (this.minControl && this.minHandleEl) {
      // always leave at full width if values are the same.
      if (range !== 0) {
        leftPos = (100 * (this.minControl.value - this.minValue)) / range;
      }
      // shift handle so it covers bar
      this.minHandleEl.nativeElement.style.left =
        this.minValue <= this.minControl.value
          ? `calc(${leftPos}% - 18px)`
          : '-6px';
    }
    if (this.maxControl && this.maxHandleEl) {
      // always leave at full width if values are the same.
      if (range !== 0) {
        rightPos = (100 * (this.maxControl.value - this.minValue)) / range;
      }
      // different offset to left so close values don't collide
      this.maxHandleEl.nativeElement.style.left =
        this.maxValue >= this.maxControl.value
          ? `calc(${rightPos}% - 6px)`
          : 'calc(100% - 6px)';
    }
    this.railHighlight.nativeElement.style.right =
      this.maxValue >= this.maxControl.value ? `${100 - rightPos}%` : '0%';
    this.railHighlight.nativeElement.style.left =
      this.minValue <= this.minControl.value ? `${leftPos}%` : '0%';

    this.changeDetectorRef.markForCheck();
  }
}

function disableSelect(e: Event): void {
  e.preventDefault();
}
